import React, { useEffect } from "react";
import { Drawer, Form, Select, DatePicker, Descriptions, message } from "antd";
import dayjs from "dayjs";
import { drawerFooter, drawerTitle } from "../../../components";

interface ChequeStatus {
  id: string;
  name: string;
  code: string;
}

interface BankAccount {
  id: string;
  account_name: string;
  balance: string | number;
}

interface ChequeStatusDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => Promise<void>;
  cheque: any | null;
  chequeStatuses: ChequeStatus[];
  bankAccounts: BankAccount[];
}

export const ChequeStatusDrawer: React.FC<ChequeStatusDrawerProps> = ({
  open,
  onClose,
  onSave,
  cheque,
  chequeStatuses,
  bankAccounts,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open || !cheque) return;
    form.resetFields();
    form.setFieldsValue({
      status_id: cheque.status_id,
      bank_account_id: cheque.bank_account_id || undefined,
      realized_date: cheque.realized_date
        ? dayjs(cheque.realized_date)
        : dayjs(),
    });
  }, [open, cheque, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onSave({
        status_id: values.status_id,
        bank_account_id: values.bank_account_id || null,
        realized_date: values.realized_date
          ? values.realized_date.format("YYYY-MM-DD HH:mm:ss")
          : null,
      });
    } catch {
      message.warning("Please fill all required fields");
    }
  };

  const clearedStatus = chequeStatuses.find((s) => s.code === "cleared");

  return (
    <Drawer
      title={drawerTitle("Update Cheque Status")}
      open={open}
      onClose={onClose}
      width={440}
      placement="right"
      footer={drawerFooter({
        actions: [
          { label: "Cancel", onClick: onClose },
          { label: "Update Status", type: "primary", onClick: handleSubmit },
        ],
      })}
    >
      {cheque && (
        <Descriptions column={1} size="small" bordered className="mb-5">
          <Descriptions.Item label="Cheque No">
            {cheque.cheque_no}
          </Descriptions.Item>
          <Descriptions.Item label="Bank">{cheque.bank_name}</Descriptions.Item>
          <Descriptions.Item label="Amount">
            LKR{" "}
            {parseFloat(cheque.amount).toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </Descriptions.Item>
          <Descriptions.Item label="Cheque Date">
            {dayjs(cheque.cheque_date).format("YYYY-MM-DD")}
          </Descriptions.Item>
          <Descriptions.Item label="Direction">
            {cheque.type === "in" ? "Inward" : "Outward"}
          </Descriptions.Item>
        </Descriptions>
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          name="status_id"
          label="New Status"
          rules={[{ required: true, message: "Required" }]}
        >
          <Select placeholder="Select status">
            {chequeStatuses.map((s) => (
              <Select.Option key={s.id} value={s.id}>
                {s.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) => prev.status_id !== curr.status_id}
        >
          {({ getFieldValue }) => {
            const isCleared =
              clearedStatus && getFieldValue("status_id") === clearedStatus.id;
            if (!isCleared) return null;

            return (
              <>
                <Form.Item
                  name="bank_account_id"
                  label="Clearance Bank Account"
                  rules={[
                    { required: true, message: "Required for cleared status" },
                  ]}
                >
                  <Select placeholder="Select bank account">
                    {bankAccounts.map((a) => (
                      <Select.Option key={a.id} value={a.id}>
                        {a.account_name} (LKR{" "}
                        {parseFloat(String(a.balance)).toLocaleString()})
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="realized_date"
                  label="Realization Date"
                  rules={[
                    { required: true, message: "Required for cleared status" },
                  ]}
                >
                  <DatePicker
                    showTime
                    className="w-full"
                    format="YYYY-MM-DD HH:mm:ss"
                  />
                </Form.Item>
              </>
            );
          }}
        </Form.Item>
      </Form>
    </Drawer>
  );
};
